
function messageIdentity(message) {
  if (message.requestId && message.role) {
    return `${message.role}:${message.requestId}`;
  }
  return `id:${message.id}`;
}

function toSortableTimestamp(message) {
  if (!message?.ts) {
    return Number.MAX_SAFE_INTEGER;
  }
  const value = Date.parse(message.ts);
  return Number.isNaN(value) ? Number.MAX_SAFE_INTEGER : value;
}

export function sortMessagesChronologically(items) {
  return items
    .map((message, index) => ({
      message,
      index,
      ts: toSortableTimestamp(message),
    }))
    .sort((left, right) => left.ts - right.ts || left.index - right.index)
    .map(({ message }) => message);
}

export function mergeMessageLists(currentMessages, incomingMessages) {
  const merged = new Map();

  for (const message of currentMessages) {
    merged.set(messageIdentity(message), message);
  }

  for (const message of incomingMessages) {
    merged.set(messageIdentity(message), message);
  }

  return sortMessagesChronologically([...merged.values()]);
}

export function buildFinalResponseMessage(payload, options = {}) {
  const {
    streamingText = '',
    currentModel = null,
    defaultModel = null,
  } = options;

  return {
    id: `response_${payload.requestId}`,
    role: 'assistant',
    text: payload.text || streamingText || '',
    imageDataUrl: payload.imageDataUrl || null,
    ts: payload.ts || new Date().toISOString(),
    requestId: payload.requestId,
    model: payload.model || currentModel || defaultModel,
    // Этап 25.2: ответило резервное звено — модель, которую просили.
    fallbackFrom: payload.fallbackFrom || null,
    // 1.27.0: ответ не дописан — 'cancelled' (остановлен) или 'error' (оборван).
    stopReason: payload.stopReason || null,
  };
}

// Последний элемент ленты, под которым стоят быстрые действия (1.27.0): ответ
// Петровича — «Копировать · Ещё раз · Поделиться», вопрос без ответа — «Повторить».
// Системные сообщения и карточки этапов не в счёт; пузырь стрима — ответ ещё пишется.
export function findQuickActionTarget(messages = []) {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (!message || message.role === 'system') {
      continue;
    }
    if (message._streaming || message._placeholder || message.role === 'summary') {
      return null;
    }
    if (message.role === 'assistant') {
      return { kind: 'answer', message };
    }
    if (message.role === 'user') {
      return { kind: 'question', message };
    }
    return null;
  }
  return null;
}

// Цитата для «Ответить» (1.27.0): Markdown-цитата начала сообщения. Длинное
// обрезается — в поле ввода важна зацепка, а не весь ответ.
export const REPLY_QUOTE_LIMIT = 280;

export function buildReplyQuote(text = '', { limit = REPLY_QUOTE_LIMIT, imageLabel = '' } = {}) {
  const source = String(text || '').trim();
  if (!source) {
    return imageLabel ? `> ${imageLabel}` : '';
  }
  const clipped = source.length > limit ? `${source.slice(0, limit).trimEnd()}…` : source;
  return clipped
    .split('\n')
    .map((line) => (line.trim() ? `> ${line}` : '>'))
    .join('\n');
}

// «В черновик» и «В композер» (К6, 1.27.2): текст дописывается ниже набранного,
// а не заменяет его — раньше черновик пропадал без вопроса.
export function appendToDraft(draft = '', text = '') {
  const addition = String(text || '');
  const current = String(draft || '').replace(/\s+$/, '');
  if (!addition) {
    return String(draft || '');
  }
  return current ? `${current}\n\n${addition}` : addition;
}

// Цитата встаёт в начало черновика, набранное остаётся ниже неё.
export function prependReplyQuote(draft = '', quote = '') {
  if (!quote) {
    return draft;
  }
  const rest = String(draft || '').replace(/^\s+/, '');
  return rest ? `${quote}\n\n${rest}` : `${quote}\n\n`;
}
